import React, { useEffect, useRef, useState } from 'react';
import { BQLink } from '../../../atoms/bqlink';
import NewTxtLabel from '../../../atoms/new-txt-label';
import Icon from '@mdi/react';
import { mdiMenuDownOutline, mdiMenu, mdiDotsGrid, mdiTextSearchVariant } from '@mdi/js';
import { handleLogoClick } from '../../../../helpers/datalayer-utils';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import DrawerMenu from '../drawer-menu';
import GlobalHeader from '../global-header';
import UserProfile from '../user-profile';
import {
  clearSSOUserDetails,
  parseSubTitle,
  getUserFirstLetter,
  getUserStatus,
} from '../../../../helpers/utils';
import { getSSOLogoutUrl, getSSORedirectUrl } from '../../../../helpers/subscription-helper';
import './main-nav.m.css';

const MainNav = ({ pageType, userData, isHeaderCheck, mainMenu }) => {
  const subMenuRef = useRef(null);
  const userProfileRef = useRef(null);
  const containerRef = useRef(null);
  const modalRef = useRef(null);
  const subMenuSectionRef = useRef(null);
  const menuWrapRef = useRef(null);
  // REMOVED: const getAppRef = useRef(null); // Causes iOS jerk

  const [appOpen, setAppOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(false);
  const [isNavSticky, setIsNavSticky] = useState(false);
  const [navOffset, setNavOffset] = useState(0);
  const [navHeight, setNavHeight] = useState(0);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [mobilePlaceholderHeight, setMobilePlaceholderHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGlobalMenu, setIsGlobalMenu] = useState(false);
  const [visibleCount, setVisibleCount] = useState(7);
  const [hamburgMenu, setHamburgMenu] = useState([]);
  const [globalNav, setGlobalNav] = useState([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [showStrip, setShowStrip] = useState(false);
  const [showText, setShowText] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [appDownloadLink, setAppDownloadLink] = useState('');

  const isSubscriber = useSelector((state) => get(state, ['userData', 'isSubscriber'], null));
  const api_endpoints = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'ndtv_header', 'api_endpoints'], null),
  );
  const SSO_LOGOUT_URL = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'subscription', 'logout_url'], null),
  );
  const integrations = useSelector((state) => get(state, ['qt', 'config', 'integrations']));
  const hamburgerObject = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'ndtv_header'], null),
  );

  // Initial setup
  useEffect(() => {
    const width = window.innerWidth;
    setIsMobile(width <= 767);
    const url = new URL(window?.location?.href);
    getSSORedirectUrl(url).then((pageUrl) => {
      setRedirectUrl(pageUrl);
    });
    let mounted = true;
    const fetchNavs = async () => {
      try {
        const [r1, r2] = await Promise.all([
          fetch(api_endpoints?.hamburgMenu),
          fetch(api_endpoints?.globalNav),
        ]);

        if (!mounted) return;

        if (r1.ok) {
          const json1 = await r1.json();
          const mainMenuData = Array.isArray(json1?.data) ? json1.data : json1;
          setHamburgMenu(mainMenuData);
        } else {
          console.error('Hamburg API failed:', r1.status);
        }

        if (r2.ok) {
          const json2 = await r2.json();
          const globalMenuData = Array.isArray(json2?.data) ? json2.data : json2;
          setGlobalNav(globalMenuData);
        } else {
          console.error('Global API failed:', r2.status);
        }
      } catch (e) {
        console.error('Failed to load navigation JSONs:', e);
      }
    };

    fetchNavs();

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
      if (width < 768) {
        setVisibleCount(mainMenu.length);
      } else if (width >= 768 && width <= 1179) {
        setVisibleCount(6);
      } else if (width > 1180 && width < 1280) {
        setVisibleCount(7);
      } else {
        setVisibleCount(8);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // FIXED: Calculate offset ONCE when component mounts or when ads are removed
  useEffect(() => {
    const calculateOffset = () => {
      if (subMenuSectionRef.current) {
        const rect = subMenuSectionRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const offset = rect.top + scrollTop;

        setNavOffset(offset);
        setNavHeight(rect.height);

        if (isMobile && menuWrapRef.current) {
          const menuWrapHeight = menuWrapRef.current.getBoundingClientRect().height;
          // FIXED: Add the topHeaderMb height for mobile
          const topHeaderMb = document.querySelector('[styleName="topHeaderMb"]');
          const topHeaderHeight = topHeaderMb ? topHeaderMb.getBoundingClientRect().height : 0;
          const totalMobileHeight = menuWrapHeight + topHeaderHeight;

          setMobilePlaceholderHeight(totalMobileHeight);
          setStickyHeight(menuWrapHeight); // Only menuWrap becomes sticky
        } else {
          setMobilePlaceholderHeight(rect.height);
          setStickyHeight(rect.height);
        }
      }
    };

    // Initial calculation
    calculateOffset();

    // Only recalculate on resize and when subscription changes
    window.addEventListener('resize', calculateOffset);

    // Delayed calculations for when ads are removed or DOM changes
    const timers = [
      setTimeout(calculateOffset, 100),
      setTimeout(calculateOffset, 500),
      setTimeout(calculateOffset, 1000),
      setTimeout(calculateOffset, 2000), // Added extra delay for slow loading
    ];

    return () => {
      window.removeEventListener('resize', calculateOffset);
      timers.forEach((t) => clearTimeout(t));
    };
  }, [isMobile, isSubscriber]); // Only recalculate on these changes

  // FIXED: Sticky navigation - SIMPLIFIED, no recalculation during scroll
  useEffect(() => {
    let ticking = false;

    const handleStickyNav = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const shouldBeSticky = scrollTop >= navOffset;

          if (shouldBeSticky !== isNavSticky) {
            setIsNavSticky(shouldBeSticky);

            if (shouldBeSticky) {
              document.documentElement.style.setProperty(
                '--nav-sticky-height',
                `${stickyHeight}px`,
              );
            } else {
              document.documentElement.style.setProperty(
                '--nav-sticky-height',
                isMobile ? '52px' : '71px',
              );
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleStickyNav, { passive: true });
    return () => window.removeEventListener('scroll', handleStickyNav);
  }, [navOffset, isNavSticky, stickyHeight, isMobile]);

  // Modal handlers
  useEffect(() => {
    if (!isModalOpen) return;

    const handleHideModal = (event) => {
      if (subMenuRef.current && !subMenuRef.current.contains(event.target)) {
        setIsModalOpen(false);
      }
    };

    window.addEventListener('scroll', handleHideModal, true);
    window.addEventListener('mousedown', handleHideModal, true);

    return () => {
      window.removeEventListener('scroll', handleHideModal, true);
      window.removeEventListener('mousedown', handleHideModal, true);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isGlobalMenu) return;

    const handleOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsGlobalMenu(false);
      }
    };

    window.addEventListener('mousedown', handleOutside, true);
    window.addEventListener('scroll', handleOutside, true);

    return () => {
      window.removeEventListener('mousedown', handleOutside, true);
      window.removeEventListener('scroll', handleOutside, true);
    };
  }, [isGlobalMenu]);

  useEffect(() => {
    if (!userProfile) return;

    const handleHideUserProfile = (event) => {
      if (userProfileRef.current && !userProfileRef.current.contains(event.target)) {
        setUserProfile(false);
      }
    };

    window.addEventListener('scroll', handleHideUserProfile, true);
    window.addEventListener('mousedown', handleHideUserProfile, true);

    return () => {
      window.removeEventListener('scroll', handleHideUserProfile, true);
      window.removeEventListener('mousedown', handleHideUserProfile, true);
    };
  }, [userProfile]);

  // FIXED: Get App Button scroll - Show/hide logic only (no iOS positioning)
  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const current = window.pageYOffset || document.documentElement.scrollTop;

          if (current < 520) {
            setShowStrip(false);
            setShowText(true);
          } else {
            setShowStrip(true);

            if (current > lastScrollY && current > 920) {
              setShowText(false);
            } else if (current < lastScrollY) {
              setShowText(true);
            }
          }

          setLastScrollY(current);
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    if (isIOS) {
      window.addEventListener('touchmove', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (isIOS) {
        window.removeEventListener('touchmove', handleScroll);
      }
    };
  }, [lastScrollY]);

  // REMOVED: iOS-specific positioning useEffect that was causing jerk
  // CSS handles positioning now via position: fixed

  // App download link detection
  useEffect(() => {
    const userAgent = window?.navigator?.userAgent;
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      setAppDownloadLink('https://apps.apple.com/in/app/ndtv-profit/id502359431');
    } else if (/Android/i.test(userAgent)) {
      setAppDownloadLink(
        'https://play.google.com/store/apps/details?id=com.ndtv.ndtvprofit&pcampaignid=web_share',
      );
    } else {
      setAppDownloadLink('/');
    }
  }, []);

  const handleMenuClick = (item) => {
    const baseData = {
      event: 'navigation_interaction',
      cta_text: item?.Title ? item?.Title : item || 'NA',
      select_type: 'header',
      page_template: pageType || 'NA',
      user_id: userData?.userProfile?.userId || 'NA',
      user_status: getUserStatus(),
      login_type: userData?.isLoggedIn ? 'logged-in' : 'logged-out',
      language_hit: 'english',
      Platform: isMobile ? 'mobile' : 'desktop',
      SiteName: 'www.ndtvprofit.com',
    };

    if (typeof window !== 'undefined' && window.dataLayer) {
      const pageviewEvent = window.dataLayer.find((d) => d.event === 'pageview');

      if (pageviewEvent) {
        baseData.page_template = pageviewEvent.page_template;
      }

      window.dataLayer.push(baseData);
    } else {
      console.error('Error: window.dataLayer is not available');
    }
  };

  const handleLogout = async () => {
    window.location = await getSSOLogoutUrl(
      integrations?.sso?.client_id,
      integrations?.sso?.client_secret,
      redirectUrl,
      SSO_LOGOUT_URL,
    );
    await clearSSOUserDetails();
  };

  const NavigationList = () => {
    if (!Array.isArray(mainMenu) || mainMenu.length === 0) {
      return null;
    }
    return (
      <div styleName="navigationList" ref={containerRef}>
        <nav styleName="menuNav">
          {isMobile ? (
            mainMenu?.map((item, index) => (
              <div onClick={() => handleMenuClick(item)} className="mobile-only" key={index}>
                <BQLink
                  href={`${item?.Link}?src=topnav`}
                  styleName="link"
                  style={{
                    fontWeight: '500',
                    color: item?.EmbedCode ? item?.EmbedCode : '#1d262f',
                  }}
                >
                  {item.Title?.toLowerCase() === 'live tv' && !isMobile && (
                    <div styleName="liveDot">
                      <span styleName="circle"></span>
                    </div>
                  )}
                  {item?.Title}
                </BQLink>
              </div>
            ))
          ) : (
            <>
              {mainMenu?.slice(0, visibleCount).map((item, index) => {
                const { text, className } = parseSubTitle(item?.SubTitle);
                return (
                  <div onClick={() => handleMenuClick(item)} className="desktop-only" key={index}>
                    <BQLink
                      href={`${item?.Link}?src=topnav`}
                      styleName="link"
                      style={{
                        fontWeight: '500',
                        color: item?.EmbedCode ? item?.EmbedCode : '#1d262f',
                      }}
                    >
                      {item.Title?.toLowerCase() === 'live tv' && (
                        <div styleName="liveDot">
                          <span styleName="circle"></span>
                        </div>
                      )}
                      {item?.Title}
                      {text && <NewTxtLabel text={text} apiClass={className} />}
                    </BQLink>
                  </div>
                );
              })}
              {mainMenu?.length > visibleCount && (
                <div styleName="moreBtnSection" className="desktop-only">
                  <button
                    styleName="moreBtn"
                    onMouseEnter={() => setIsModalOpen(true)}
                    onMouseLeave={() => setIsModalOpen(false)}
                    aria-label="More links"
                  >
                    <Icon path={mdiMenu} size="20px" color="#000000" />
                    <Icon path={mdiMenuDownOutline} size="18px" color="#000000" />

                    {isModalOpen && (
                      <div styleName="modalContent" onClick={(e) => e.stopPropagation()}>
                        <ul styleName="hiddenLinks">
                          {mainMenu?.slice(visibleCount).map((item, idx) => (
                            <li key={idx} onClick={() => handleMenuClick(item)}>
                              <BQLink
                                href={`${item?.Link}?src=topnav`}
                                styleName="link modalLink"
                                style={{
                                  fontWeight: '500',
                                  color: item?.EmbedCode ? item?.EmbedCode : '#1d262f',
                                }}
                              >
                                {item.Title?.toLowerCase() === 'live tv' && (
                                  <div styleName="liveDot">
                                    <span styleName="circle"></span>
                                  </div>
                                )}
                                {item?.Title}
                              </BQLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </button>
                  <span
                    styleName="btnHide"
                    onMouseEnter={() => setIsModalOpen(true)}
                    onMouseLeave={() => setIsModalOpen(false)}
                  ></span>
                </div>
              )}
            </>
          )}
        </nav>
      </div>
    );
  };

  const HomeSection = ({ className }) => {
    return (
      <div styleName="homeSection" className={className}>
        <div onClick={() => setIsOpen((prev) => !prev)}>
          <Icon path={mdiTextSearchVariant} size="26px" color="#000000" styleName="menu-icon" />
        </div>
        <div onClick={() => handleLogoClick(pageType, userData, isMobile)}>
          <BQLink href="/?src=header">
            <div style={{ height: '28px', width: '160px' }}>
              <img src="/icons/ndtv_profit_black.svg" width="160px" height="28px" alt="user" />
            </div>
          </BQLink>
        </div>
      </div>
    );
  };

  const BrandSection = () => {
    if (!(isMobile && !isNavSticky)) {
      return null;
    }

    return (
      <div styleName="brandSection" className="mobile-only">
        <div styleName="userWrap" onClick={() => setUserProfile((prev) => !prev)}>
          <div styleName="userBorder">
            {getUserFirstLetter(userData?.userProfile) ? (
              <div styleName="userLetter">{getUserFirstLetter(userData?.userProfile)}</div>
            ) : (
              <img src="/icons/user.svg" width="14px" height="14px" alt="user" />
            )}
          </div>
          {userProfile && (
            <div ref={userProfileRef}>
              <UserProfile logout={handleLogout} pageType={pageType} userData={userData} />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {isNavSticky && (
        <div
          style={{
            height: `${isMobile ? mobilePlaceholderHeight : navHeight}px`,
          }}
          aria-hidden="true"
        />
      )}

      <div styleName={`subMenuSection ${isNavSticky ? 'is-sticky' : ''}`} ref={subMenuSectionRef}>
        <div styleName="subMenuWrapper">
          {!isNavSticky && (
            <div styleName="topHeaderMb">
              <HomeSection className="mobile-only" />
              {BrandSection()}
            </div>
          )}

          <div styleName="menuWrap" ref={menuWrapRef}>
            <HomeSection className="desktop-only" />
            {NavigationList()}
          </div>

          {!isMobile && (
            <div styleName="socialWrap">
              <div
                styleName="globalNav"
                onMouseEnter={() => setIsGlobalMenu(true)}
                onMouseLeave={() => setIsGlobalMenu(false)}
              >
                <Icon path={mdiDotsGrid} size="16px" color="#000000" />
                {isHeaderCheck?.globalNav && isGlobalMenu && (
                  <GlobalHeader data={globalNav} setIsGlobalMenu={setIsGlobalMenu} />
                )}
                <span
                  styleName={`hide ${isGlobalMenu ? 'view' : ''}`}
                  onMouseEnter={() => setIsGlobalMenu(true)}
                  onMouseLeave={() => setIsGlobalMenu(false)}
                ></span>
              </div>
              <div styleName="userWrap" onClick={() => setUserProfile((prev) => !prev)}>
                <div styleName="userBorder">
                  {getUserFirstLetter(userData?.userProfile) ? (
                    <div styleName="userLetter">{getUserFirstLetter(userData?.userProfile)}</div>
                  ) : (
                    <img src="/icons/user.svg" width="14px" height="14px" alt="user" />
                  )}
                </div>
                {userProfile && (
                  <div ref={userProfileRef}>
                    <UserProfile logout={handleLogout} pageType={pageType} userData={userData} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          styleName={isNavSticky ? 'get-app stickyApp' : 'get-app'}
          onMouseEnter={() => setAppOpen(true)}
          onMouseLeave={() => setAppOpen(false)}
          className="desktop-only"
        >
          <img src="/icons/get-app.svg" width="24px" height="24px" alt="getapp" />
          Get App
          {appOpen && (
            <div styleName="barcode">
              <div styleName="banner">
                <img
                  src="/icons/get-app.svg"
                  width="18px"
                  height="18px"
                  alt="get"
                  styleName="get"
                />
                <img src="/icons/app-scanner.png" width="83px" height="83px" alt="barcode" />
              </div>
              <div styleName="name-txt">Scan to Download</div>
            </div>
          )}
        </div>

        {showStrip && (
          <BQLink
            href={appDownloadLink}
            target="_blank"
            styleName="get-app stickyAppMb"
            className="mobile-only"
          >
            <img src="/icons/get-app.svg" width="24" height="24" alt="getapp" />
            {showText && <span>Get App</span>}
          </BQLink>
        )}
      </div>

      {isHeaderCheck?.hamburgMenu && (
        <DrawerMenu
          data={hamburgMenu}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          userData={userData}
          pageType={pageType}
          logout={handleLogout}
          hamburgerList={hamburgerObject}
          isSubscriber={isSubscriber}
        />
      )}
    </>
  );
};

export default MainNav;
